import React, { useEffect, useMemo, useState } from 'react'
import { Switch } from '../ui'
import type { WatchlistEntityType } from '../../services/watchlist'
import type {
  NotificationPreferenceUpdate,
  NotificationPreferencesData,
  NotificationTypePreference,
} from '../../types/notifications'
import {
  applyPreferenceUpdate,
  filterPreferencesForScope,
  itemMasterUpdate,
  itemSubtypeUpdate,
  userMasterUpdate,
  userSubtypeUpdate,
  WATCHLIST_MASTER_LABEL,
  watchlistMasterType,
} from '../../utils/notificationPreferences'

interface NotificationPreferencesEditorProps {
  types: NotificationTypePreference[]
  scope: 'user' | 'item'
  /** Required for item scope — filters subtypes to entity-relevant rows. */
  entityType?: WatchlistEntityType
  onSave: (updates: NotificationPreferenceUpdate[]) => Promise<NotificationPreferencesData>
  className?: string
}

/**
 * Master + subtype toggles for watchlist notifications.
 */
const NotificationPreferencesEditor: React.FC<NotificationPreferencesEditorProps> = ({
  types,
  scope,
  entityType,
  onSave,
  className = '',
}) => {
  const visibleData = useMemo(
    () => filterPreferencesForScope({ types }, scope, entityType),
    [types, scope, entityType]
  )

  const [localData, setLocalData] = useState<NotificationPreferencesData>(visibleData)
  const [busyKey, setBusyKey] = useState<string | null>(null)

  useEffect(() => {
    setLocalData(visibleData)
  }, [visibleData])

  async function apply(update: NotificationPreferenceUpdate, key: string) {
    const previous = localData
    setLocalData(applyPreferenceUpdate(localData, update))
    setBusyKey(key)
    try {
      const next = await onSave([update])
      setLocalData(filterPreferencesForScope(next, scope, entityType))
    } catch {
      setLocalData(previous)
    } finally {
      setBusyKey(null)
    }
  }

  const watchlist = localData.types.find((t) => t.type === watchlistMasterType())

  if (!watchlist) {
    return (
      <p className="text-sm text-ink-500">Ingen notifikationstyper tilgængelige.</p>
    )
  }

  const masterKey = `${watchlist.type}:master`
  const masterOn = watchlist.enabled

  return (
    <div className={`space-y-3 ${className}`}>
      <PreferenceRow
        label={WATCHLIST_MASTER_LABEL}
        master
        checked={masterOn}
        disabled={busyKey !== null}
        onChange={(on) => {
          void apply(
            scope === 'user'
              ? userMasterUpdate(on)
              : itemMasterUpdate(on ? true : false),
            masterKey
          )
        }}
      />

      {watchlist.subtypes.length > 0 && (
        <ul
          className={`space-y-0.5 pl-4 transition-opacity duration-150 ${
            masterOn ? 'opacity-100' : 'opacity-45'
          }`}
        >
          {watchlist.subtypes.map((sub) => {
            const key = `${watchlist.type}:${sub.subtype}`
            const disabled =
              busyKey !== null ||
              !masterOn ||
              (scope === 'item' && !masterOn)

            return (
              <li key={sub.subtype}>
                <PreferenceRow
                  label={sub.label}
                  nested
                  checked={sub.enabled}
                  disabled={disabled}
                  onChange={(on) => {
                    void apply(
                      scope === 'user'
                        ? userSubtypeUpdate(sub.subtype, on)
                        : itemSubtypeUpdate(sub.subtype, on),
                      key
                    )
                  }}
                />
              </li>
            )
          })}
        </ul>
      )}

      {scope === 'user' &&
        localData.types
          .filter((t) => t.type !== watchlistMasterType())
          .map((type) => (
            <PreferenceRow
              key={type.type}
              label={type.label}
              checked={type.enabled}
              disabled
              hint="Kommer snart"
              onChange={() => {}}
            />
          ))}
    </div>
  )
}

function PreferenceRow({
  label,
  checked,
  disabled,
  master = false,
  nested = false,
  hint,
  onChange,
}: {
  label: string
  checked: boolean
  disabled?: boolean
  master?: boolean
  nested?: boolean
  hint?: string
  onChange: (checked: boolean) => void
}) {
  const id = React.useId()
  return (
    <div
      className={`flex items-center justify-between gap-3 rounded-sm transition-colors hover:bg-paper-200/50 ${
        master ? 'py-1.5' : nested ? 'py-1 pl-3' : 'py-1.5'
      }`}
    >
      <label
        htmlFor={id}
        className={`min-w-0 flex-1 select-none ${
          disabled && !hint ? 'cursor-default' : 'cursor-pointer'
        }`}
      >
        <span
          className={`block leading-snug ${
            master
              ? 'text-sm font-medium text-ink'
              : nested
                ? 'text-xs text-ink-700'
                : 'text-sm text-ink'
          } ${disabled && !hint ? 'text-ink-400' : ''}`}
        >
          {label}
        </span>
        {hint ? (
          <span className="mt-0.5 block text-[11px] text-ink-400">{hint}</span>
        ) : null}
      </label>
      <Switch id={id} checked={checked} disabled={disabled} onChange={onChange} />
    </div>
  )
}

export default NotificationPreferencesEditor
